import { CallMetricFilters } from '../call-metric-filters/call-metric-filters';
import styles from './surveys-header.module.scss';
import RefreshArrow from '@/assets/icons/refresh-ccw-01.svg?react';
import Download1 from '@/assets/icons/download-04.svg?react';
import Search from '@/assets/icons/search-md.svg?react';
import { Card, Flex, Input, Tooltip } from 'antd';
import { Button } from '@/components/button/button';
import { useMemo, useState } from 'react';
import { debounce } from '@/utils/debounce/debounce';
import Dropdown from '../form/dropdown/dropdown';
import InfoCircle from '@/assets/icons/info-circle.svg?react';
import { useUser } from '@/hooks/use-user/use-user';
import { CustomTableHeader } from '../custom-table-header/custom-table-header';
import {
  CONVERSATION_TYPE_OPTIONS,
  SCORE_OPTIONS,
  SCORE_TYPE_OPTIONS,
  SEARCH_DELAY,
} from './surveys-header.constants.ts';
import Icon from '@ant-design/icons';
import { SurveysFilters } from '@/services/surveys-service/surveys-service.types';
import { Score } from '../score/score';
import clsx from 'clsx';
import { CustomRangePicker } from '../custom-range-picker/custom-range-picker.tsx';
import dayjs, { Dayjs } from 'dayjs';
import { CustomFilters } from '../custom-filters/custom-filters.tsx';
import { useScreenSize } from '@/hooks/use-screen-size/use-screen-size.tsx';

interface SurveysHeaderProps {
  surveysNumber: number;
  companiesList: string[];
  averageScore: number;
  onRefreshData: () => void;
  filters: SurveysFilters;
  onChangeFilters: (filters: SurveysFilters) => void;
  dates: [Dayjs | null, Dayjs | null] | null;
  setDates: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  onDateRangeClear: () => void;
  onChangeDateRangeFilter: (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => void;
  onSearchQueryChange: (query: string) => void;
  onExportHistory: () => void;
  searchResults?: string[];
  isSearchLoading?: boolean;
  searchError?: Error | null;
  isCompanySelectDisabled?: boolean;
}

export const SurveysHeader = ({
  surveysNumber,
  onRefreshData,
  filters,
  onChangeFilters,
  companiesList,
  averageScore,
  dates,
  setDates,
  onDateRangeClear,
  onChangeDateRangeFilter,
  onSearchQueryChange,
  onExportHistory,
  searchResults,
  isSearchLoading,
  searchError,
  isCompanySelectDisabled,
}: SurveysHeaderProps) => {
  const [filterValue, setFilterValue] = useState(filters.query || '');
  const { userData, isFetched } = useUser();

  const isBayonne = userData?.company?.name.toLocaleLowerCase() === 'Bayonne'.toLowerCase();
  const screenSize = useScreenSize();
  const isMobile = screenSize.width < 768;

  const debouncedFilterChange = useMemo(
    () => debounce(onChangeFilters, SEARCH_DELAY),
    [onChangeFilters]
  );

  const handleQueryChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(value);
    debouncedFilterChange({ ...filters, query: value });
  };

  const handleConversationTypeChange = (value: string) => {
    onChangeFilters({ ...filters, conversation_type: value });
  };

  const handleScoreChange = (value: number) => {
    onChangeFilters({ ...filters, score: value });
  };

  const handleScoreTypeChange = (value: string) => {
    onChangeFilters({ ...filters, score_type: value });
  };

  return (
    <Card className={styles.container} style={{ padding: '16px 24px' }}>
      <Flex vertical>
        <Flex wrap="wrap" gap={20} className={styles.topHeaderContainer}>
          <Flex className={styles.surveysCountSectionContainer}>
            <Flex gap={30} align="center">
              <Button
                aria-label="Refresh"
                buttonVariant="secondary"
                onClick={onRefreshData}
                icon={<RefreshArrow stroke={styles.iconColor} />}
              ></Button>
              <CustomTableHeader
                textHeader1="Number of"
                textHeader2="surveys"
                count={surveysNumber}
              />
            </Flex>

            <Flex className={styles.filtersSectionContainer}>
              {!isBayonne && isFetched && (
                <CallMetricFilters
                  value={{ time_range: filters.time_range }}
                  onChange={(value) => {
                    onChangeFilters({
                      ...filters,
                      time_range: value.time_range || '7d',
                      start_time: undefined,
                      end_time: undefined,
                    });
                    setDates([null, null]);
                  }}
                />
              )}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <CustomRangePicker
                  dates={dates}
                  setDates={setDates}
                  onClear={onDateRangeClear}
                  format="YYYY-MM-DD"
                  onChange={onChangeDateRangeFilter}
                  className={
                    filters.start_time !== undefined && filters.end_time !== undefined
                      ? styles.activeDatePicker
                      : ''
                  }
                  value={
                    filters.start_time && filters.end_time
                      ? [dayjs(filters.start_time), dayjs(filters.end_time)]
                      : [undefined, undefined]
                  }
                />
              </div>
            </Flex>
          </Flex>

          <Flex className={styles.dropdownContainer} gap={10}>
            <Dropdown
              className={clsx(styles.dropdown, styles.conversationTypeDropdown)}
              wrapperClassName={styles.wrapperClassName}
              dropdownListClass={styles.typeDropdownList}
              onChange={handleConversationTypeChange}
              placeholder="Conversation type"
              options={CONVERSATION_TYPE_OPTIONS}
              value={filters.conversation_type}
            />

            <Dropdown
              wrapperClassName={styles.wrapperClassName}
              className={styles.dropdown}
              dropdownListClass={styles.scoreDropdownList}
              onChange={handleScoreChange}
              placeholder="Score"
              options={SCORE_OPTIONS}
              value={filters.status}
            />
            <Dropdown
              wrapperClassName={styles.wrapperClassName}
              className={styles.dropdown}
              dropdownListClass={styles.scoreDropdownList}
              onChange={handleScoreTypeChange}
              placeholder="Score type"
              options={SCORE_TYPE_OPTIONS}
              value={filters.score_type}
            />

            {isFetched && (
              <CustomFilters
                customClassName={styles.customSelectClassName}
                companiesList={companiesList}
                filters={filters}
                defaultCompanyValue={undefined}
                onChangeFilters={(updatedFilters) => {
                  onChangeFilters({
                    ...filters,
                    company_name: updatedFilters.company_name,
                    topic: undefined,
                  });
                }}
                onSearchQueryChange={onSearchQueryChange}
                searchResults={searchResults}
                isSearchLoading={isSearchLoading}
                searchError={searchError}
                disabled={isCompanySelectDisabled}
              />
            )}
          </Flex>
        </Flex>

        <Flex
          gap={20}
          className={clsx(
            styles.bottomHeaderContainer,
            surveysNumber == 0 && styles.searchSectionContainerWithoutScore
          )}
          wrap="wrap"
          justify="space-between"
          align="center"
        >
          {surveysNumber > 0 && (
            <Card className={styles.averageScore}>
              <Flex vertical>
                <p className={styles.averageTitle}>
                  Average CSAT Score
                  <Tooltip
                    title={
                      'The average score from customer satisfaction surveys, calculated based on responses to questions about customer satisfaction.'
                    }
                  >
                    <Icon
                      component={() => <InfoCircle fill="none" height="18" width="18" />}
                      className={styles.averageScoreInfoIcon}
                    />
                  </Tooltip>
                </p>
                <span className={styles.averageSubtitle}>Overall Satisfaction</span>
              </Flex>

              <div>
                <Score score={averageScore} />
              </div>
            </Card>
          )}

          <Flex
            justify="space-between"
            gap={20}
            className={clsx(
              styles.searchSectionContainer,
              surveysNumber == 0 && styles.searchSectionContainerWithoutScore
            )}
          >
            <Flex vertical gap={5} className={styles.searchContainer}>
              <Input
                onChange={(event) => handleQueryChange(event)}
                value={filterValue}
                placeholder="Search"
                prefix={
                  <Search stroke={styles.iconColor} height={16} width={16} strokeWidth={2.5} />
                }
              />

              <Flex gap={5}>
                <Button
                  rightIcon
                  onClick={onExportHistory}
                  icon={<Download1 stroke={styles.iconColor} style={{ width: 10, height: 10 }} />}
                  style={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: styles.buttonColor,
                    padding: isMobile ? '0px 5px' : '0px 12px',
                  }}
                  buttonVariant="tertiary"
                >
                  <Tooltip title="Prepare and download the call history for a selected period.">
                    Export
                  </Tooltip>
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};
